import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  Box,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import Layout from '../components/Layout';
import attendanceService from '../services/attendanceService';
import employeeService from '../services/employeeService';
import '../styles/ShiftManagement.css';

const ShiftManagementPage = () => {
  // State management
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeeShifts, setEmployeeShifts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // View state
  const [activeTab, setActiveTab] = useState(0); // 0: shifts, 1: assignments

  // Dialog state
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Form state for shift
  const [shiftForm, setShiftForm] = useState({
    name: '',
    start_time: '09:00',
    end_time: '18:00',
    grace_period_minutes: 15,
    is_active: true
  });

  // Form state for assignment
  const [assignForm, setAssignForm] = useState({
    employee_id: '',
    shift_id: ''
  });

  // Statistics
  const [stats, setStats] = useState({
    totalShifts: 0,
    activeShifts: 0,
    totalEmployees: 0,
    assignedEmployees: 0
  });

  // Fetch data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch shifts
      const shiftsResponse = await attendanceService.getAllShifts();
      setShifts(shiftsResponse.shifts || []);

      // Fetch employees
      const employeesResponse = await employeeService.getAllEmployees();
      const emps = employeesResponse.employees || [];
      setEmployees(emps);

      // Fetch each employee's shift assignment
      const shiftAssignments = {};
      for (const emp of emps) {
        try {
          const shiftResponse = await attendanceService.getEmployeeShift(emp.id);
          if (shiftResponse.shift) {
            shiftAssignments[emp.id] = shiftResponse.shift;
          }
        } catch (err) {
          // Employee might not have a shift assigned yet
          console.log(`No shift for employee ${emp.id}`);
        }
      }
      setEmployeeShifts(shiftAssignments);

      // Calculate statistics
      const assignedCount = Object.keys(shiftAssignments).length;
      setStats({
        totalShifts: shiftsResponse.shifts?.length || 0,
        activeShifts: shiftsResponse.shifts?.filter(s => s.is_active).length || 0,
        totalEmployees: emps.length,
        assignedEmployees: assignedCount
      });

      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to load shift data');
      setLoading(false);
    }
  };

  // Handle shift dialog
  const openShiftDialog = (shift = null) => {
    if (shift) {
      setEditingShift(shift);
      setShiftForm({
        name: shift.name,
        start_time: shift.start_time.substring(0, 5), // HH:MM
        end_time: shift.end_time.substring(0, 5),
        grace_period_minutes: shift.grace_period_minutes,
        is_active: shift.is_active
      });
    } else {
      setEditingShift(null);
      setShiftForm({
        name: '',
        start_time: '09:00',
        end_time: '18:00',
        grace_period_minutes: 15,
        is_active: true
      });
    }
    setShiftDialogOpen(true);
  };

  const closeShiftDialog = () => {
    setShiftDialogOpen(false);
    setEditingShift(null);
    setShiftForm({
      name: '',
      start_time: '09:00',
      end_time: '18:00',
      grace_period_minutes: 15,
      is_active: true
    });
  };

  const handleShiftFormChange = (field, value) => {
    setShiftForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitShift = async () => {
    if (!shiftForm.name.trim()) {
      setError('Shift name is required');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const shiftData = {
        name: shiftForm.name,
        start_time: shiftForm.start_time + ':00', // Add seconds
        end_time: shiftForm.end_time + ':00',
        grace_period_minutes: parseInt(shiftForm.grace_period_minutes),
        is_active: shiftForm.is_active
      };

      if (editingShift) {
        await attendanceService.updateShift(editingShift.id, shiftData);
        setSuccess(`Shift "${shiftForm.name}" updated successfully!`);
      } else {
        await attendanceService.createShift(shiftData);
        setSuccess(`Shift "${shiftForm.name}" created successfully!`);
      }

      closeShiftDialog();
      fetchAllData(); // Refresh data
      setProcessing(false);

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error saving shift:', err);
      setError(err.response?.data?.message || 'Failed to save shift');
      setProcessing(false);
    }
  };

  const handleDeleteShift = async (shift) => {
    if (!window.confirm(`Are you sure you want to delete shift "${shift.name}"?`)) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      await attendanceService.deleteShift(shift.id);
      setSuccess(`Shift "${shift.name}" deleted successfully!`);
      fetchAllData(); // Refresh data
      setProcessing(false);

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error deleting shift:', err);
      setError(err.response?.data?.message || 'Failed to delete shift');
      setProcessing(false);
    }
  };

  // Handle assignment dialog
  const openAssignDialog = () => {
    setAssignForm({
      employee_id: '',
      shift_id: ''
    });
    setAssignDialogOpen(true);
  };

  const closeAssignDialog = () => {
    setAssignDialogOpen(false);
    setAssignForm({
      employee_id: '',
      shift_id: ''
    });
  };

  const handleAssignFormChange = (field, value) => {
    setAssignForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitAssignment = async () => {
    if (!assignForm.employee_id || !assignForm.shift_id) {
      setError('Please select both employee and shift');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      await attendanceService.assignShift(
        parseInt(assignForm.employee_id),
        parseInt(assignForm.shift_id)
      );
      
      const employee = employees.find(e => e.id === parseInt(assignForm.employee_id));
      const shift = shifts.find(s => s.id === parseInt(assignForm.shift_id));
      
      setSuccess(`Assigned ${employee?.full_name || employee?.username} to ${shift?.name}!`);
      closeAssignDialog();
      fetchAllData(); // Refresh data
      setProcessing(false);

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error assigning shift:', err);
      setError(err.response?.data?.message || 'Failed to assign shift');
      setProcessing(false);
    }
  };

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString.substring(0, 5); // HH:MM
  };

  // Calculate shift duration
  const calculateDuration = (start, end) => {
    const startTime = new Date(`2000-01-01T${start}`);
    const endTime = new Date(`2000-01-01T${end}`);
    let diff = (endTime - startTime) / (1000 * 60 * 60); // Hours
    if (diff < 0) diff += 24; // Handle overnight shifts
    return `${diff} hours`;
  };

  // Render statistics cards
  const renderStatsCards = () => {
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stats-card stats-card-total">
            <CardContent sx={{ textAlign: 'center', color: 'white' }}>
              <Typography variant="h3" sx={{ fontSize: '2.5rem', mb: 1 }}>
                ⏰
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, my: 2, fontSize: '2.5rem' }}>
                {stats.totalShifts}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                Total Shifts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stats-card stats-card-active">
            <CardContent sx={{ textAlign: 'center', color: 'white' }}>
              <Typography variant="h3" sx={{ fontSize: '2.5rem', mb: 1 }}>
                ✅
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, my: 2, fontSize: '2.5rem' }}>
                {stats.activeShifts}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                Active Shifts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stats-card stats-card-employees">
            <CardContent sx={{ textAlign: 'center', color: 'white' }}>
              <Typography variant="h3" sx={{ fontSize: '2.5rem', mb: 1 }}>
                👥
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, my: 2, fontSize: '2.5rem' }}>
                {stats.totalEmployees}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                Total Employees
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stats-card stats-card-assigned">
            <CardContent sx={{ textAlign: 'center', color: 'white' }}>
              <Typography variant="h3" sx={{ fontSize: '2.5rem', mb: 1 }}>
                📋
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, my: 2, fontSize: '2.5rem' }}>
                {stats.assignedEmployees}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                Assigned Employees
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Render shifts table
  const renderShiftsTable = () => {
    if (shifts.length === 0) {
      return (
        <Alert severity="info">
          No shifts created yet. Click "Create New Shift" to get started.
        </Alert>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Shift Name</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>End Time</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Grace Period</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shifts.map((shift) => (
              <TableRow key={shift.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimeIcon color="primary" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {shift.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={formatTime(shift.start_time)} color="info" size="small" />
                </TableCell>
                <TableCell>
                  <Chip label={formatTime(shift.end_time)} color="info" size="small" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {calculateDuration(shift.start_time, shift.end_time)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={`${shift.grace_period_minutes} mins`} 
                    color="warning" 
                    size="small" 
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip 
                    label={shift.is_active ? 'Active' : 'Inactive'} 
                    color={shift.is_active ? 'success' : 'default'} 
                    size="small" 
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => openShiftDialog(shift)}
                      title="Edit Shift"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteShift(shift)}
                      title="Delete Shift"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render assignments table
  const renderAssignmentsTable = () => {
    const assignedEmployees = employees.filter(emp => employeeShifts[emp.id]);
    const unassignedEmployees = employees.filter(emp => !employeeShifts[emp.id]);

    if (employees.length === 0) {
      return <Alert severity="info">No employees found.</Alert>;
    }

    return (
      <>
        {/* Assigned Employees */}
        <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
          Assigned Employees ({assignedEmployees.length})
        </Typography>
        {assignedEmployees.length > 0 ? (
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee Name</TableCell>
                  <TableCell>Employee Code</TableCell>
                  <TableCell>Assigned Shift</TableCell>
                  <TableCell>Shift Timing</TableCell>
                  <TableCell>Grace Period</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignedEmployees.map((emp) => {
                  const shift = employeeShifts[emp.id];
                  return (
                    <TableRow key={emp.id} hover>
                      <TableCell>{emp.full_name || emp.username}</TableCell>
                      <TableCell>
                        <Chip label={emp.emp_code} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip label={shift.name} color="primary" size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${shift.grace_period_minutes} mins`} 
                          color="warning" 
                          size="small" 
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info" sx={{ mb: 4 }}>No employees assigned to shifts yet.</Alert>
        )}

        {/* Unassigned Employees */}
        {unassignedEmployees.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
              Unassigned Employees ({unassignedEmployees.length})
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee Name</TableCell>
                    <TableCell>Employee Code</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unassignedEmployees.map((emp) => (
                    <TableRow key={emp.id} hover>
                      <TableCell>{emp.full_name || emp.username}</TableCell>
                      <TableCell>
                        <Chip label={emp.emp_code} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label="No Shift Assigned" color="error" size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </>
    );
  };

  // Main render
  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, borderBottom: 2, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
            Shift Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create work shifts, manage timings, and assign shifts to employees
          </Typography>
        </Box>

        {/* Success/Error Messages */}
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
            <CircularProgress size={60} />
            <Typography sx={{ mt: 3 }}>Loading shift data...</Typography>
          </Box>
        )}

        {/* Data Display */}
        {!loading && (
          <>
            {/* Statistics Cards */}
            {renderStatsCards()}

            {/* Action Buttons */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => openShiftDialog()}
              >
                Create New Shift
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AssignIcon />}
                onClick={openAssignDialog}
                disabled={shifts.length === 0 || employees.length === 0}
              >
                Assign Shift to Employee
              </Button>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="fullWidth"
              >
                <Tab label="⏰ Work Shifts" />
                <Tab label="👥 Employee Assignments" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Card>
              <CardHeader 
                title={activeTab === 0 ? 'Work Shifts' : 'Employee Shift Assignments'}
              />
              <CardContent>
                {activeTab === 0 && renderShiftsTable()}
                {activeTab === 1 && renderAssignmentsTable()}
              </CardContent>
            </Card>
          </>
        )}

        {/* Create/Edit Shift Dialog */}
        <Dialog open={shiftDialogOpen} onClose={closeShiftDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingShift ? '✏️ Edit Shift' : '➕ Create New Shift'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Shift Name"
                value={shiftForm.name}
                onChange={(e) => handleShiftFormChange('name', e.target.value)}
                placeholder="e.g., Morning Shift, Evening Shift"
                required
              />
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="time"
                    label="Start Time"
                    value={shiftForm.start_time}
                    onChange={(e) => handleShiftFormChange('start_time', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="time"
                    label="End Time"
                    value={shiftForm.end_time}
                    onChange={(e) => handleShiftFormChange('end_time', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                type="number"
                label="Grace Period (minutes)"
                value={shiftForm.grace_period_minutes}
                onChange={(e) => handleShiftFormChange('grace_period_minutes', e.target.value)}
                inputProps={{ min: 0, max: 60 }}
                helperText="Employees can check in this many minutes late without penalty"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={shiftForm.is_active}
                    onChange={(e) => handleShiftFormChange('is_active', e.target.checked)}
                    color="primary"
                  />
                }
                label="Active Shift"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeShiftDialog} disabled={processing}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitShift}
              variant="contained"
              color="primary"
              disabled={processing}
              startIcon={processing ? <CircularProgress size={20} /> : null}
            >
              {processing ? 'Saving...' : (editingShift ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Assign Shift Dialog */}
        <Dialog open={assignDialogOpen} onClose={closeAssignDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            📋 Assign Shift to Employee
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Select Employee</InputLabel>
                <Select
                  value={assignForm.employee_id}
                  label="Select Employee"
                  onChange={(e) => handleAssignFormChange('employee_id', e.target.value)}
                >
                  <MenuItem value="">-- Select Employee --</MenuItem>
                  {employees.map(emp => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.full_name || emp.username} ({emp.emp_code})
                      {employeeShifts[emp.id] && (
                        <Chip 
                          label={`Currently: ${employeeShifts[emp.id].name}`} 
                          size="small" 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Select Shift</InputLabel>
                <Select
                  value={assignForm.shift_id}
                  label="Select Shift"
                  onChange={(e) => handleAssignFormChange('shift_id', e.target.value)}
                >
                  <MenuItem value="">-- Select Shift --</MenuItem>
                  {shifts.filter(s => s.is_active).map(shift => (
                    <MenuItem key={shift.id} value={shift.id}>
                      {shift.name} ({formatTime(shift.start_time)} - {formatTime(shift.end_time)})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAssignDialog} disabled={processing}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAssignment}
              variant="contained"
              color="secondary"
              disabled={processing}
              startIcon={processing ? <CircularProgress size={20} /> : <AssignIcon />}
            >
              {processing ? 'Assigning...' : 'Assign Shift'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default ShiftManagementPage;
